import {
  useAttachmentFileBulkCreateMutation,
  useContactPersonReadQuery,
  useCreateSupplierOfferMutation,
  usePurchaseOrderByIdQuery,
  useUploadEmailMutation,
} from 'api';
import PostalMime from 'postal-mime';
import { Form, Upload } from 'components';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { SupplierOfferSourceValueType, SupplierOfferType } from 'models/SupplierOffer';
import { CLIENT_EMAIL_ATTACHMENT_TYPE, MAX_EML_FILE_SIZE, MAX_FILE_SIZE } from 'constants/index';

const validAttachmentFormats = ['image/jpeg', 'image/png', 'text/csv', 'application/pdf'];

export const useAddSupplierOffer = (props: {
  onModalStateChange: () => void;
  values: Record<string, any>[];
  type: SupplierOfferType;
  source: SupplierOfferSourceValueType;
  entityId?: string;
}) => {
  const [form] = Form.useForm();
  const { onModalStateChange, values, type, source, entityId } = props;
  const [fileList, setFileList] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailFile, setEmailFile] = useState<File | undefined>();
  const [attachmentType, setAttachmentType] = useState<string | undefined>();
  const [openContactPersonModal, setOpenContactPersonModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<string | undefined>();
  const [attachments, setAttachments] = useState<{ type: string; file: any }[]>([]);

  const navigate = useNavigate();
  const { handleSuccess, handleError, handleWarning } = useNotificationMessages();
  const [uploadEmail] = useUploadEmailMutation();
  const { data = {} } = usePurchaseOrderByIdQuery({ id: entityId }, { skip: !entityId });
  const { purchaseOrder = {} } = data;
  const [createSupplierOffer] = useCreateSupplierOfferMutation();
  const [createBulkFiles] = useAttachmentFileBulkCreateMutation();
  const { data: contactPersons = [], isLoading: isLoadingContactPerson } = useContactPersonReadQuery(
    { entityId: selectedSupplier || '' },
    { skip: !selectedSupplier }
  );
  const onEmailRemove = useCallback(() => {
    setAttachments((oState) => oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE));
    setEmailFile(undefined);
  }, []);

  const handleFormChange = useCallback(
    (values: Record<string, any>) => {
      if (values.supplierId) {
        setSelectedSupplier(values.supplierId);
        form.resetFields(['contactPersonId']);
      }
    },
    [form]
  );

  const handleEmailUpload = useCallback(
    async (file: File) => {
      if (file.size > MAX_EML_FILE_SIZE) {
        form.setFieldValue('emailFile', null);
        form.setFields([
          {
            name: 'emailFile',
            errors: [`File exceeds the maximum size of 20 MB.`],
          },
        ]);
        setAttachments((oState) => oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE));
        handleError('File exceeds the maximum size of 20 MB.');
        return Upload.LIST_IGNORE;
      }
      const arrayBuffer = await file.arrayBuffer();
      const parser = new PostalMime();
      const parsedEmail = await parser.parse(arrayBuffer);
      let attachments: any[] = [];
      // Create blob URLs for inline attachments (e.g., images)
      const cidMap: Record<string, string> = {};
      parsedEmail.attachments?.forEach((att: any) => {
        if (att.contentId && att.disposition === 'inline') {
          const blob = new Blob([att.content], { type: att.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          // Strip <> from contentId if present
          const cid = att.contentId.replace(/^<|>$/g, '');
          cidMap[cid] = blobUrl;
        }
      });

      // Replace cid: URLs in HTML with blob URLs
      let emailHtml = parsedEmail.html || `<pre>${parsedEmail.text}</pre>`;
      for (const [cid, url] of Object.entries(cidMap)) {
        const regex = new RegExp(`cid:${cid}`, 'g');
        emailHtml = emailHtml.replace(regex, url);
      }

      const files: File[] = parsedEmail.attachments
        .map((att) => {
          return new File([att.content], att.filename || 'attachment', {
            type: att.mimeType || 'application/octet-stream',
          });
        })
        .filter((file) => {
          return validAttachmentFormats?.includes(file.type) && file.size <= MAX_FILE_SIZE;
        });

      if (parsedEmail.attachments.length > files.length) {
        handleWarning(
          'Attachments that exceed 5 GB or are in an unsupported file format are not added to the attachments section.'
        );
      }
      if (files.length) {
        setAttachments((oState) => {
          attachments = [
            ...oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE),
            ...files.map((file) => ({ type: CLIENT_EMAIL_ATTACHMENT_TYPE, file: file })),
          ];
          return attachments;
        });
      } else {
        setAttachments((oState) => {
          attachments = oState.filter((i) => i.type !== CLIENT_EMAIL_ATTACHMENT_TYPE);
          return attachments;
        });
      }
      setEmailFile(file);
      return false; // Prevent default upload behavior
    },
    [handleWarning, handleError, form]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    setAttachments([]);
    setFileList([]);
    setAttachmentType(undefined);
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish = useCallback(async () => {
    const payload = form.getFieldsValue() || {};
    payload.emailFile = undefined;
    payload.relatedEntityId = entityId;
    payload.relatedEntityUuid = purchaseOrder.uuid;
    payload.supplierOfferItems = values.map((item: Record<string, any>) => {
      return {
        item: {
          brandId: item.brand.id,
          itemNumber: item.itemId,
          uuid: item.itemUuid,
          sku: item.itemSku,
          description: item.description,
          quantity: item.quantity,
        },
      };
    });
    payload.supplierOfferType = type;
    payload.attachmentTypes = attachments.map((i) => i.type);
    try {
      setIsLoading(true);
      if (emailFile) {
        const emailFormData = new FormData();
        emailFormData.append('file', emailFile as any);
        const emailResp = await uploadEmail(emailFormData);
        if (emailResp?.error) {
          handleError(emailResp?.error);
          return;
        }
        payload.emailId = emailResp?.data?.data;
        payload.attachmentTypes.push('EMAIL');
      }
      const { data, error }: any = await createSupplierOffer(payload);
      if (data?.success) {
        if (Array.isArray(data.data.attachmentIds) && data.data.attachmentIds.length) {
          const form = new FormData();
          form.append('attachmentIds', data.data.attachmentIds.join(','));
          attachments.forEach((att) => form.append('files', att.file));
          if (emailFile) {
            form.append('files', emailFile);
          }
          form.append('serviceName', 'WRD');
          // TODO entity name should be changed
          await createBulkFiles({ data: form, entityName: 'PURCHASE_REQUEST' });
        }
        handleSuccess(`Supplier Offer was successfully created.`);
        let supplierOfferUrl = ADMIN_ROUTES.purchaseOrder.supplierOfferUpdate
          .replace(':source', source)
          .replace(':supplierOfferId', data.data.id);
        if (entityId) {
          supplierOfferUrl = supplierOfferUrl.replace(':entityId', entityId);
        }
        navigate(supplierOfferUrl);
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }, [
    createSupplierOffer,
    handleSuccess,
    handleError,
    values,
    form,
    attachments,
    onCancel,
    createBulkFiles,
    emailFile,
    uploadEmail,
    navigate,
    type,
    source,
    entityId,
    purchaseOrder,
  ]);

  return {
    form,
    onCancel,
    onFinish,
    isLoading,
    handleFormChange,
    contactPersons,
    selectedSupplier,
    isLoadingContactPerson,
    openContactPersonModal,
    setOpenContactPersonModal,
    setAttachments,
    attachments,
    handleEmailUpload,
    onEmailRemove,
    setFileList,
    fileList,
    attachmentType,
    setAttachmentType,
  };
};
